import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Copy, Check } from 'lucide-react';
import type { AskAiCellValue } from '@/types/askAi';

const CELL_CHAR_CAP = 200;
const numberFmt = new Intl.NumberFormat('en-ZA');

interface Props {
  columns: string[];
  rows: AskAiCellValue[][];
}

function isNumericColumn(rows: AskAiCellValue[][], colIdx: number): boolean {
  let seen = 0;
  for (const row of rows) {
    const v = row[colIdx];
    if (v === null || v === undefined) continue;
    seen += 1;
    if (typeof v !== 'number') return false;
    if (seen >= 8) break;
  }
  return seen > 0;
}

function renderCell(v: AskAiCellValue, isNumeric: boolean): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number' && isNumeric) return numberFmt.format(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  const s = String(v);
  return s.length > CELL_CHAR_CAP ? `${s.slice(0, CELL_CHAR_CAP)}…` : s;
}

function escapeCsvCell(v: AskAiCellValue): string {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'string' ? v : String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toCsv(columns: string[], rows: AskAiCellValue[][]): string {
  const head = columns.map(escapeCsvCell).join(',');
  const body = rows.map((r) => r.map(escapeCsvCell).join(',')).join('\n');
  return `${head}\n${body}`;
}

export function AskAiResultTable({ columns, rows }: Props) {
  const [copied, setCopied] = useState(false);

  const numericFlags = useMemo(
    () => columns.map((_, i) => isNumericColumn(rows, i)),
    [columns, rows],
  );

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(toCsv(columns, rows));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard write may fail under non-secure contexts; silently swallow
    }
  };

  if (columns.length === 0) {
    return <p className="text-xs text-muted-foreground">No columns returned.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="max-h-72 overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c, i) => (
                <TableHead
                  key={c}
                  className={cn(
                    'whitespace-nowrap',
                    numericFlags[i] && 'text-right',
                  )}
                >
                  {c}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-4 text-center text-xs text-muted-foreground"
                >
                  No rows.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, ri) => (
              <TableRow key={ri}>
                {row.map((cell, ci) => (
                  <TableCell
                    key={ci}
                    className={cn(
                      'whitespace-nowrap text-xs',
                      numericFlags[ci] && 'text-right font-mono',
                    )}
                    title={cell == null ? '' : String(cell)}
                  >
                    {renderCell(cell, numericFlags[ci])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
      >
        {copied ? (
          <>
            <Check className="h-3 w-3" /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" /> Copy as CSV
          </>
        )}
      </button>
    </div>
  );
}
